import { isNil } from '../object'
import { isNoOpMetric, NoOpMetric } from './no-op'
import type { MetricData } from './numeric'
import type { GeneralMetric, NullableMetric } from './shared'

/**
 * A phantom type that guarantees uniqueness. Since it's not exported, it's not possible to create a value
 * that has this type unless you use the public api to create it.
 */
declare const averageableTag: unique symbol

type AverageableTag = typeof averageableTag
type AverageConstraint = MetricData<number> & { weight?: number }
type Data<T extends AverageConstraint> = T & {
  weight: number
  __averageableTag: AverageableTag
}
type Maybe<T extends AverageConstraint> = Data<T> | Nothing
type Nothing = typeof NoOpMetric.Number

/**
 * This interface is intended to be broadly compliant with a higher-order interface
 * that I struggled to get TypeScript to understand. But the general idea is that
 * all metrics should have a create, fromNullable, combine, reduce, and hasData method,
 * and they should behave equivalently with respect to their own base type.
 */
interface Averageable {
  create<T extends AverageConstraint>(metric: T): Data<T>
  fromNullable<T extends NullableMetric>(
    metric?: T
  ): Maybe<NonNullable<T> & { value: number; weight: number }>
  combine(a: Nothing, b: Nothing): Nothing
  combine<T extends AverageConstraint>(a: Data<T>, b: Data<T>): Data<T>
  combine<T extends AverageConstraint>(a: Maybe<T>, b: Maybe<T>): Maybe<T>
  combine<T extends AverageConstraint>(a: Maybe<T>, b: Maybe<T>): Maybe<T>
  reduce<T extends AverageConstraint>(metrics: Maybe<T>[]): Maybe<T>
  hasData<T extends AverageConstraint>(metric: Maybe<T>): metric is Data<T>
}

/**
 * A utility class for working with averageable metrics.
 *
 * @example
 * const metric = AverageableMetric.create({ label: 'test', value: 1, weight: 2 })
 * AverageableMetric.hasData(metric) // true
 *
 * const metric2 = AverageableMetric.fromNullable(null)
 * AverageableMetric.hasData(metric2) // false
 *
 * const metric3 = AverageableMetric.combine(metric, metric2) // same as metric (referential equality)
 * AverageableMetric.hasData(metric3) // true
 *
 * const metric4 = AverageableMetric.combine(metric2, metric2) // same value as metric2, but double the weight
 *
 */
export const Averageable: Averageable = {
  /**
   * Create a new SummableMetric from a metric object.
   */
  create(metric) {
    return { ...metric, weight: metric.weight ?? 1 } as typeof metric & {
      __averageableTag: AverageableTag
      weight: number
    }
  },

  /**
   * Create a new SummableMetric from a nullable metric object.
   *
   * If the metric is null or undefined, or has a null value,
   * a NoOpMetric is returned.
   */
  fromNullable(metric) {
    if (isNil(metric) || typeof metric.value !== 'number') {
      return NoOpMetric.Number
    }

    // typescript doesn't seem to be able to infer this correctly without the cast
    return Averageable.create(metric) as any
  },

  /**
   * Add two summable metrics together, returning a new metric.
   *
   * If either metric is a NoOpMetric, the other metric is returned.
   * If both metrics are NoOpMetrics, a NoOpMetric is returned.
   *
   */
  combine<T extends AverageConstraint>(a: Maybe<T>, b: Maybe<T>): Maybe<T> {
    if (isNoOpMetric(a)) {
      return b
    }
    if (isNoOpMetric(b)) {
      return a
    }
    const weight = a.weight + b.weight

    return Averageable.create({
      ...a,
      value: (a.value * a.weight + b.value * b.weight) / weight,
      weight
    })
  },

  /**
   * Sum an array of summable metrics together, returning a new metric.
   * The returned metric may be a NoOpMetric if all metrics are NoOpMetrics,
   * or if the array is empty.
   */
  reduce<T extends AverageConstraint>(metrics: Maybe<T>[]) {
    return metrics.reduce((sum, metric) => Averageable.combine(sum, metric), NoOpMetric.Number)
  },

  /**
   * Check if a metric has data.
   */
  hasData<T extends AverageConstraint>(metric: Maybe<T>): metric is Data<T> {
    return !isNoOpMetric(metric)
  }
} satisfies GeneralMetric<AverageConstraint, Data<AverageConstraint>, Nothing, NullableMetric>

export type AverageableMetric<T extends AverageConstraint = AverageConstraint> = Data<T>
